import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SHOP_DISCREET_NOTICE, centsLabel, type ShopProduct as Product } from "@gooner/shared";
import { track } from "../../analytics";
import { userApi } from "../../lib/api";
import { addToShopCart, useShopCart } from "../../lib/shopCart";
import { AddToCartButton } from "../components/AddToCartButton";
import { Button, buttonClass } from "../ui/primitives";
import { ShopBackLink } from "../components/ShopBackLink";

/** "400: That one is sold out" reads better without the status. */
const errorText = (err: unknown) =>
  err instanceof Error ? err.message.replace(/^\d{3}:\s*/, "") : "Checkout didn't start. Try again.";

/** `/shop/:slug` — the photos, the words, the price, and the button into the cart. */
export function ShopProduct() {
  const { slug = "" } = useParams();
  const cart = useShopCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setProduct(null);
    setLoadError(null);
    setShown(0);
    userApi
      .shopProduct(slug)
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
        track("shop_product_viewed", { slug: p.slug, cents: p.priceCents });
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(errorText(err));
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loadError) {
    return (
      <div className="mx-auto max-w-3xl p-6 py-16 text-center">
        <p className="text-sm text-zinc-500">{loadError}</p>
        <Link to="/shop" className="mt-4 inline-block text-sm text-zinc-300 hover:text-zinc-100">
          Back to the shop
        </Link>
      </div>
    );
  }
  // Holds the page's shape while it loads, so nothing jumps when the product lands.
  if (!product) return <div className="mx-auto min-h-[80dvh] max-w-5xl p-4 sm:p-6" />;

  const inCart = cart.find((l) => l.slug === product.slug)?.quantity ?? 0;
  const add = () => {
    addToShopCart(product.slug);
    track("shop_cart_added", { slug: product.slug, cents: product.priceCents, from: "product" });
  };

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <ShopBackLink />
      <div className="mt-3 grid gap-6 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:gap-10">
        <section aria-label="Product photos">
          <div className="aspect-[4/5] overflow-hidden rounded-lg bg-[var(--color-panel-2)]">
            {product.images[shown] ? (
              <img src={product.images[shown]} alt={product.name} className="h-full w-full object-contain" />
            ) : null}
          </div>
          {product.images.length > 1 ? (
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
              {product.images.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  aria-label={`Show photo ${i + 1}`}
                  aria-current={i === shown}
                  onClick={() => setShown(i)}
                  className={`h-16 w-14 shrink-0 overflow-hidden rounded border-2 ${
                    i === shown ? "border-[var(--color-accent)]" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          ) : null}
        </section>

        <section>
          <h1 className="text-2xl font-semibold text-zinc-100">{product.name}</h1>
          {product.tagline ? <p className="mt-1 text-zinc-400">{product.tagline}</p> : null}
          <p className="mt-4 flex flex-wrap items-baseline gap-x-3">
            <span className="text-3xl font-semibold text-[var(--color-accent)]">{centsLabel(product.priceCents)}</span>
            {product.compareAtCents > product.priceCents ? (
              <>
                <span className="text-lg text-zinc-500 line-through">{centsLabel(product.compareAtCents)}</span>
                <span className="text-sm font-medium text-[var(--color-accent-soft)]">
                  Save {centsLabel(product.compareAtCents - product.priceCents)}
                </span>
              </>
            ) : null}
          </p>
          <p className="mt-1 text-sm text-zinc-500">Shipping included. US addresses only.</p>
          <p className="text-sm text-zinc-500">{SHOP_DISCREET_NOTICE}</p>

          {product.soldOut ? (
            <Button className="mt-5 w-full" disabled>
              Sold out
            </Button>
          ) : (
            <>
              <AddToCartButton variant="primary" className="mt-5 w-full" onAdd={add} />
              {inCart > 0 ? (
                <Link to="/shop/cart" className={buttonClass("subtle", "md", "mt-2 w-full")}>
                  View cart ({inCart} of this)
                </Link>
              ) : null}
            </>
          )}

          {product.description ? (
            <p className="mt-6 max-w-prose whitespace-pre-line text-sm leading-relaxed text-zinc-300">
              {product.description}
            </p>
          ) : null}
        </section>
      </div>
    </div>
  );
}
